/**
 * Structural skeletons — the shared vocabulary for "this has not arrived yet".
 *
 * The `<Skeleton>` next door is a single grey block. These are the shapes
 * pages are supposed to build placeholders out of, and they exist because a
 * page that hand-rolls its own shimmer invariably gets the GEOMETRY wrong:
 * it draws three bars where the real component renders a heading, two fields
 * and a footer, the box comes out 80px short, and everything below it jumps
 * when the data lands. A skeleton whose height is a guess is not a loading
 * state, it is a layout shift with a nice texture.
 *
 * So each part here is sized from the same tokens the real component uses —
 * the same text sizes, the same gaps, the same row heights — and the sizes
 * live in one place where they can be kept honest. `<SkeletonTable>` in
 * particular takes the SAME column template the real table is given, so the
 * placeholder's columns land on the real table's columns to the pixel.
 *
 * All of it is `aria-hidden`: a screen reader gets `aria-busy` from the
 * region that owns the loading state (see `<StablePresence>`), never a
 * description of grey rectangles.
 */
import { cn } from "@/lib/cn";
import { Skeleton } from "./skeleton";

/** One line of text. `w` is a Tailwind width class — vary it across lines so
 *  a paragraph placeholder does not read as a solid block. */
export function SkeletonLine({
  w = "w-full",
  /** Matches the app's text sizes: body 14px, small 12px, meta 11px. */
  size = "body",
  className,
}: {
  w?: string;
  size?: "meta" | "small" | "body" | "title";
  className?: string;
}) {
  const h = {
    meta: "h-2.5",
    small: "h-3",
    body: "h-3.5",
    title: "h-5",
  }[size];
  return <Skeleton className={cn(h, w, "rounded-[3px]", className)} />;
}

/** A paragraph: N lines, last one short, on the real leading. */
export function SkeletonText({
  lines = 3,
  size = "small",
  className,
}: {
  lines?: number;
  size?: "meta" | "small" | "body";
  className?: string;
}) {
  const widths = ["w-[92%]", "w-[97%]", "w-[85%]", "w-[94%]"];
  return (
    <div className={cn("space-y-2", className)} aria-hidden>
      {Array.from({ length: lines }).map((_, i) => (
        <SkeletonLine
          key={i}
          size={size}
          w={i === lines - 1 ? "w-[60%]" : widths[i % widths.length]}
        />
      ))}
    </div>
  );
}

/**
 * A labelled form field. Mirrors `<Field>` + `<Input>`: an 11.5px label, a
 * 6px gap and a 34px control — which is why a settings form built out of
 * these does not move when the account data lands.
 */
export function SkeletonField({
  labelWidth = "w-24",
  className,
}: {
  labelWidth?: string;
  className?: string;
}) {
  return (
    <div className={cn("space-y-1.5", className)} aria-hidden>
      <SkeletonLine size="meta" w={labelWidth} />
      <Skeleton className="h-[34px] w-full rounded-lg" />
    </div>
  );
}

/**
 * A section: the heading row (with its optional action slot) plus a body.
 *
 * The action slot is reserved by default because the real `<SectionHeader>`
 * usually gets one, and a heading that grows an action button after the fact
 * is the exact pop-in this vocabulary exists to prevent.
 */
export function SkeletonSection({
  titleWidth = "w-40",
  action = true,
  children,
  className,
}: {
  titleWidth?: string;
  action?: boolean;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("space-y-3", className)} aria-hidden>
      <div className="flex items-center justify-between gap-3 min-h-[24px]">
        <SkeletonLine size="small" w={titleWidth} />
        {action && <Skeleton className="h-[26px] w-24 rounded-lg shrink-0" />}
      </div>
      {children}
    </div>
  );
}

/**
 * A table placeholder that lines up with the real table.
 *
 * `template` is the SAME CSS grid template the DataTable will render with —
 * pass it and the placeholder's columns are the real table's columns, so the
 * header and the first row do not slide sideways when the rows arrive. Given
 * nothing, it falls back to equal columns, which is better than a solid bar
 * but is a guess: prefer passing the template.
 */
export function SkeletonTable({
  rows = 8,
  columns = 5,
  template,
  header = true,
  className,
}: {
  rows?: number;
  columns?: number;
  /** e.g. "minmax(0,1fr) 120px 96px" — the real table's grid template. */
  template?: string;
  header?: boolean;
  className?: string;
}) {
  const style = { gridTemplateColumns: template ?? `repeat(${columns}, minmax(0,1fr))` };
  const cells = template ? template.trim().split(/\s+(?![^(]*\))/).length : columns;
  return (
    <div className={cn("w-full", className)} aria-hidden>
      {header && (
        <div
          className="grid gap-3 px-3 h-9 items-center border-b border-line-subtle"
          style={style}
        >
          {Array.from({ length: cells }).map((_, i) => (
            <SkeletonLine key={i} size="meta" w={i === 0 ? "w-28" : "w-16"} />
          ))}
        </div>
      )}
      {Array.from({ length: rows }).map((_, r) => (
        <div
          key={r}
          // 40px matches the DataTable's row height — the number that decides
          // whether the page below the table moves when it fills in.
          className="grid gap-3 px-3 h-10 items-center border-b border-line-subtle/50"
          style={style}
        >
          {Array.from({ length: cells }).map((_, c) => (
            <SkeletonLine key={c} size="small" w={c === 0 ? "w-[70%]" : "w-[45%]"} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** A status pill placeholder on the pill's exact metrics, so a row of them
 *  reserves the real line height. */
export function SkeletonPill({ w = "w-16" }: { w?: string }) {
  return <Skeleton className={cn("h-[18px] rounded-full", w)} />;
}
